<script setup lang="ts">
import { computed } from 'vue';
import { Swords, Mountain, Trophy, MoreHorizontal } from 'lucide-vue-next';

// 胶水集成：使用 shadcn-vue 组件构建场景选择器
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

/**
 * 奇穴使用场景选择器组件
 * @description 使用 RadioGroup 优化场景选择交互
 */

interface Props {
  modelValue: string;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const scenes = [
  { 
    id: 'zhanchang', 
    name: '战场', 
    description: '大规模团战场景',
    icon: Swords
  },
  { 
    id: 'juejing', 
    name: '绝境战场', 
    description: '吃鸡模式',
    icon: Mountain
  },
  { 
    id: 'jingjichang', 
    name: '竞技场', 
    description: 'JJC 2v2/3v3/5v5',
    icon: Trophy
  },
  { 
    id: 'qita', 
    name: '其他', 
    description: '插旗、野外、攻防等其他场景',
    icon: MoreHorizontal
  }
];

const selectedScene = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value)
});
</script>

<template>
  <RadioGroup v-model="selectedScene" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div
      v-for="scene in scenes"
      :key="scene.id"
      :class="[
        'relative flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200',
        selectedScene === scene.id
          ? 'border-primary bg-primary/5 shadow-md shadow-primary/10'
          : 'border-border/60 bg-card hover:border-primary/30 hover:bg-secondary/30'
      ]"
      @click="selectedScene = scene.id"
    >
      <RadioGroupItem 
        :id="scene.id" 
        :value="scene.id"
        class="mt-0.5 border-primary/50 data-[state=checked]:border-primary data-[state=checked]:bg-primary"
      />
      
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-1">
          <component 
            :is="scene.icon" 
            class="w-4 h-4"
            :class="selectedScene === scene.id ? 'text-primary' : 'text-muted-foreground'"
          />
          <Label 
            :for="scene.id" 
            class="font-semibold cursor-pointer"
            :class="selectedScene === scene.id ? 'text-primary' : 'text-foreground'"
          >
            {{ scene.name }}
          </Label>
        </div>
        <p class="text-xs text-muted-foreground leading-relaxed">
          {{ scene.description }}
        </p>
      </div>
      
      <!-- 选中指示器 -->
      <div 
        v-if="selectedScene === scene.id"
        class="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary animate-fade-in"
      ></div>
    </div>
  </RadioGroup>
</template>
